import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AllDataBreeding, fetchAllDataBreedings } from "../../../api/breeding";
import { enumsectionToI18nKey } from "../../../generated/enums";
import { useAccountStorage } from "../../../hooks/useAccountStorage";
import { useAdvancedSearch } from "../../../hooks/useAdvancedSearch";
import { CSVField, downloadCSV } from "../../../utils/csv";
import { TICKET_SIDE, TicketColumn } from "../../common/TableDetails";
import { TableFilters } from "../../common/TableFilters";
import { Header } from "../../Header";
import { sectionToIcon } from "../../type/default";
import { BreedingCard } from "./BreedingCard";
import { BreedingsTableContent } from "./BreedingsTableContent";
import { BreedingTicket } from "./BreedingTicket";

const useWindowHeight = () => {
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return height;
};

export const BreedingsTable = () => {
  const windowHeight = useWindowHeight();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBreeding, setSelectedBreeding] =
    useState<AllDataBreeding | null>(null);
  const [breedings, setBreedings] = useState<AllDataBreeding[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const { getAccountStorage } = useAccountStorage();
  const accountStorage = getAccountStorage();
  const canSeeCount =
    accountStorage?.type === "admin" || accountStorage?.type === "psc";

  const loadBreedings = async () => {
    const { breedings: fetchedBreedings, error: fetchError } =
      await fetchAllDataBreedings();
    setBreedings(fetchedBreedings);
    setError(fetchError);
    setIsLoading(false);
  };

  const refreshBreedings = async () => {
    const { breedings: fetchedBreedings, error: fetchError } =
      await fetchAllDataBreedings();
    setBreedings(fetchedBreedings);
    setError(fetchError);

    if (selectedBreeding) {
      const updated = fetchedBreedings.find(
        (entry) => entry.breeding.id === selectedBreeding.breeding.id
      );
      if (updated) {
        setSelectedBreeding(updated);
      }
    }
  };

  useEffect(() => {
    loadBreedings();
  }, []);

  const getSearchableFields = (entry: AllDataBreeding) => {
    return [
      entry.breeding.name,
      entry.company.company_name,
      entry.breeding.city,
      entry.breeding.country,
      entry.breeding.address,
      ...entry.buildings.map((building) => building.name),
      ...entry.devices.map((device) => device.name),
      ...entry.devices.map((device) => device.type),
    ];
  };

  const filteredBreedings = useAdvancedSearch({
    searchTerm,
    data: breedings,
    fields: getSearchableFields,
  });

  const handleDownload = () => {
    const fields: CSVField<AllDataBreeding>[] = [
      {
        header: t("breeding-name"),
        key: (data) => data.breeding.name,
      },
      {
        header: t("section-company"),
        key: (data) => data.company.company_name,
      },
      {
        header: t("section-building"),
        key: (data) =>
          data.buildings.map((building) => building.name).join(", "),
      },
      {
        header: t("section-devices"),
        key: (data) =>
          data.devices.map((device) => device.name || device.type).join(", "),
      },
    ];

    downloadCSV(filteredBreedings, fields, "breedings");
  };

  return (
    <div className="w-screen md:w-full flex overflow-x-clip md:overflow-hidden">
      <div
        className={`w-full ${TICKET_SIDE} ${
          selectedBreeding ? "lg:overflow-x-auto" : ""
        }`}
      >
        <div className="p-4">
          <Header
            title={t(enumsectionToI18nKey["section_breedings"])}
            icon={sectionToIcon.section_breedings}
          />
          {isLoading ? (
            <p className="mt-6 text-tertiary">{t("loading")}</p>
          ) : error ? (
            <p className="mt-6 text-red-500">{error}</p>
          ) : (
            <div className="mt-3 rounded-lg">
              <TableFilters
                filterValue=""
                onFilterChange={() => undefined}
                filterOptions={[]}
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                onDownload={handleDownload}
                showResultCount={canSeeCount}
                resultCount={filteredBreedings.length}
              />
              <div className="md:hidden space-y-4">
                {filteredBreedings.map((entry) => (
                  <BreedingCard
                    key={entry.breeding.id}
                    data={entry}
                    onSelect={() => setSelectedBreeding(entry)}
                  />
                ))}
              </div>

              <div className="hidden md:block">
                <BreedingsTableContent
                  breedings={filteredBreedings}
                  windowHeight={windowHeight}
                  onSelectBreeding={setSelectedBreeding}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <TicketColumn open={!!selectedBreeding}>
        {selectedBreeding && (
          <BreedingTicket
            key={selectedBreeding.breeding.id}
            breeding={selectedBreeding.breeding}
            onClose={() => setSelectedBreeding(null)}
            onDataRefresh={refreshBreedings}
            useBackIcon={false}
          />
        )}
      </TicketColumn>
    </div>
  );
};
